import {
  TCallManualReviewApiResData,
  TEvaluationDetailsData,
} from "@/api/evaluation-details/evaluation-details.types";
import { type StateCreator } from "zustand";
import { type RefetchOptions } from "@tanstack/react-query";

export type TAudioStampData = {
  startTimestamp: number | null;
  endTimestamp?: number | null;
  shouldPlay: boolean;
};

export type TAudioSource = string;
export type TRefetchCallback = (options?: RefetchOptions) => Promise<unknown>;

export type TEvaluationDetailStoreState = {
  // audio related variables
  isGlobalCurrentAudioPlaying: string;
  audioTimeStampData: TAudioStampData;
  isCallReviewInternalAudioPlaying: string | null;
  currentAudioSource: TAudioSource | null;
  shouldPauseAllAudio: boolean;
  isAudioBufferLoading: boolean;
  // tab click handling
  isAiEvaluationTabClick: boolean;
  isCallInsightTabClick: boolean;
  isSentimentTabClick: boolean;
  /** Unique key for the currently active call-review question (e.g. "categoryId-questionIndex") so only one shows as playing across all accordions */
  activeCallReviewQuestionKey: string | null;
  callReviewActiveAudioSource: "conversation" | "conversation-drawer";
  // call Evalution detail api data
  callEvalutionDetailsData: TEvaluationDetailsData | undefined;
  manualReviewData: TCallManualReviewApiResData[];
  updatedManualReviewData: TCallManualReviewApiResData[];
  isCurrentUserRoleAllowedForNextWorkflow: boolean;
  refetchEvaluationDetails?: TRefetchCallback;
  refetchManualReview?: TRefetchCallback;
  refetchWorkflowLevelStatus?: TRefetchCallback;
} & Partial<Record<string, any>>;

export type TEvaluationDetailStoreActions = {
  setIsGlobalAudioPlaying: (value: string) => void;
  setGlobalAudioStopped?: (value: string) => void;
  setAudioTimeStampData: (value: TAudioStampData) => void;
  setCallReviewInternalAudioPlaying: (value: string | null) => void;
  setCallReviewInternalAudioStopped?: (value: string) => void;
  setCurrentAudioSource: (source: TAudioSource | null) => void;
  setShouldPauseAllAudio: (shouldPause: boolean) => void;
  setActiveCallReviewQuestionKey: (key: string | null) => void;
  setCallReviewActiveAudioSource: (
    source: "conversation" | "conversation-drawer",
  ) => void;
  setIsAiEvaluationTabClick: (clicked: boolean) => void;
  setIsCallInsightTabClick: (clicked: boolean) => void;
  setIsSentimentTabClick: (clicked: boolean) => void;
  setIsCurrentUserRoleAllowedForNextWorkflow: (isAllowed: boolean) => void;
  setRefetchEvaluationDetails: (callback?: TRefetchCallback) => void;
  setRefetchManualReview: (callback?: TRefetchCallback) => void;
  setRefetchWorkflowLevelStatus: (callback?: TRefetchCallback) => void;
  setIsAudioBufferLoading: (isLoading: boolean) => void;
  setCallEvalutionDetailsData: (
    payload: TEvaluationDetailsData | undefined,
  ) => void;
  setManualReviewData: (payload: TCallManualReviewApiResData[]) => void;
  setUpdatedManualReviewData: (payload: TCallManualReviewApiResData[]) => void;
};

const initialEvaluationDetailStoreState: TEvaluationDetailStoreState = {
  isGlobalCurrentAudioPlaying: "",
  isCallReviewInternalAudioPlaying: null,
  audioTimeStampData: {
    startTimestamp: null,
    endTimestamp: null,
    shouldPlay: false,
  },
  audioCurentTimeStamp: null,
  isAiEvaluationTabClick: false,
  isCallInsightTabClick: false,
  isSentimentTabClick: false,
  isCurrentUserRoleAllowedForNextWorkflow: false,
  refetchEvaluationDetails: undefined,
  refetchManualReview: undefined,
  refetchWorkflowLevelStatus: undefined,
  currentAudioSource: null,
  shouldPauseAllAudio: false,
  activeCallReviewQuestionKey: null,
  callReviewActiveAudioSource: "conversation",
  isAudioBufferLoading: false,
  callEvalutionDetailsData: undefined,
  manualReviewData: [],
  updatedManualReviewData: [],
};

export type TEvaluationDetailStore = TEvaluationDetailStoreState &
  TEvaluationDetailStoreActions;

export const createEvaluationDetailSlice: StateCreator<
  TEvaluationDetailStoreState
> = (set) => ({
  ...initialEvaluationDetailStoreState,
  setIsGlobalAudioPlaying: (value: string) =>
    set({ isGlobalCurrentAudioPlaying: value }),
  setGlobalAudioStopped: (value: string) =>
    set({ isGlobalCurrentAudioPlaying: value }),
  setAudioTimeStampData: (value: TAudioStampData) =>
    set({ audioTimeStampData: value }),
  setCallReviewInternalAudioPlaying: (value: string | null) =>
    set({ isCallReviewInternalAudioPlaying: value }),
  setCallReviewInternalAudioStopped: (value: string) =>
    set({ isCallReviewInternalAudioPlaying: value }),
  setCurrentAudioSource: (source: TAudioSource | null) =>
    set({ currentAudioSource: source }),
  setShouldPauseAllAudio: (shouldPause: boolean) =>
    set({ shouldPauseAllAudio: shouldPause }),
  setActiveCallReviewQuestionKey: (key: string | null) =>
    set({ activeCallReviewQuestionKey: key }),
  setCallReviewActiveAudioSource: (
    source: "conversation" | "conversation-drawer",
  ) => set({ callReviewActiveAudioSource: source }),
  setIsAiEvaluationTabClick: (clicked: boolean) =>
    set({ isAiEvaluationTabClick: clicked }),
  setIsCallInsightTabClick: (clicked: boolean) =>
    set({ isCallInsightTabClick: clicked }),
  setIsSentimentTabClick: (clicked: boolean) =>
    set({ isSentimentTabClick: clicked }),
  setIsCurrentUserRoleAllowedForNextWorkflow: (isAllowed: boolean) =>
    set({ isCurrentUserRoleAllowedForNextWorkflow: isAllowed }),
  setRefetchEvaluationDetails: (callback?: TRefetchCallback) =>
    set({ refetchEvaluationDetails: callback }),
  setRefetchManualReview: (callback?: TRefetchCallback) =>
    set({ refetchManualReview: callback }),
  setRefetchWorkflowLevelStatus: (callback?: TRefetchCallback) =>
    set({ refetchWorkflowLevelStatus: callback }),
  setIsAudioBufferLoading: (isLoading: boolean) =>
    set({ isAudioBufferLoading: isLoading }),
  setCallEvalutionDetailsData: (payload: TEvaluationDetailsData | undefined) =>
    set({ callEvalutionDetailsData: payload }),
  setManualReviewData: (payload: TCallManualReviewApiResData[]) =>
    set({ manualReviewData: payload }),
  setUpdatedManualReviewData: (payload: TCallManualReviewApiResData[]) =>
    set({ updatedManualReviewData: payload }),
});
